

import { Component, Vue, Watch, Prop } from "vue-property-decorator"
import { namespace, Getter, Action } from "vuex-class"

import { DataDefinitionWithFields, FieldInstance } from "@/types"

// @ts-ignore
import draggable from "vuedraggable"
import vSelect from "vue-select"
import { fieldTypesExcludeFromRuleSet } from "@/types/dataType"

@Component({
	components: {
		draggable,
		"v-select": vSelect
	}
})
export default class DefinitionTitle extends Vue {
	@Prop()
	public definition!: DataDefinitionWithFields;

	@Prop()
	public fields!: any[];

	@Prop()
	public value!: string;

	@Prop({ type: Boolean, default: false })
	public allowEmpty!: boolean

	@Prop({ type: Boolean, default: true })
	public allowFieldRemovals!: boolean

	@Prop({ type: Boolean, default: true })
	public showFieldSelector!: boolean

	@Prop()
	public invalidFeedback!: string;

	@Prop({ type: String, default: "Title Format: " })
	public label!: string;

	@Prop({ type: String, default: "A title format is how to show each item. It's also used for searching when no field is specified" })
	public description!: string;

	@Prop({ type: Boolean, default: false })
	public readonly!: boolean

	renderedValue: string = "";
	startText: string = "";
	fieldDisplay: any = [];

	get definitionTitleFormat () {
		return this.startText + this.fieldDisplay.map((field: any) => "{" + field.id + "}" + field.postText).join("")
	}

	get definitionFields () {
		if (!this.definition) {
			if (this.fields) return this.fields

			return []
		}

		return this.definition.fields.filter(f => f.field.dataType &&
			f.field.dataType !== "text-area" &&
			!fieldTypesExcludeFromRuleSet.includes(f.field.dataType))
	}

	get fieldOptions () {
		return this.definitionFields.map(f => {
			return {
				id: f.field.id,
				label: f.field.title
			}
		})
	}

	mounted () {
		this.setValue(this.value ? this.value : "")
	}

	@Watch("value")
	onValueChanged (value: string) {
		this.setValue(value || "")
	}

	@Watch("definitionFields", { deep: true })
	definitionFieldsChanged (fields: FieldInstance[]) {
		let i = 0
		while (i < this.fieldDisplay.length) {
			const field = this.fieldDisplay[i]
			const matchingFields = fields.filter(f => f.field.id === field.id)
			if (matchingFields.length === 0) {
				this.fieldDisplay.splice(i, 1)

				if (i === 0) {
					this.startText += field.postText
				} else {
					this.fieldDisplay[i - 1].postText += field.postText
				}
			} else {
				field.title = matchingFields[0].field.title
				++i
			}
		}
	}

	setValue (value: string) {
		if (value === this.renderedValue) {
			return
		}
		this.renderedValue = value

		if (this.value) {
			const splitSections = this.value.split("{")

			this.startText = splitSections[0]
			this.fieldDisplay = []

			for (let i = 1; i < splitSections.length; i++) {
				const section = splitSections[i].split("}")
				const fieldId = section[0]

				const field = this.definitionFields.firstOrDefault((f: FieldInstance) => f.field.id === fieldId)

				if (field && field.field) {
					this.fieldDisplay.push({
						id: fieldId,
						title: field.field.title,
						postText: section[1]
					})
				}
			}
		} else {

		}
	}

	@Watch("definitionTitleFormat")
	onTitleFormatChanged (value: string) {
		this.$emit("input", value)
	}

	fieldStyle (fieldText: string) {
		if (!fieldText || fieldText.length < 3) {
			return {
				"width": "36px"
			}
		}

		return {
			"width": ((fieldText.length + 4) * 6) + "px"
		}
	}

	addField (field: FieldInstance) {
		const nextIndex = this.fieldDisplay.length

		this.fieldDisplay.push({
			id: field.field.id,
			title: field.field.title,
			postText: ""
		})

		Vue.nextTick().then(() => {
			const inputElement = (this.$refs["text-input-" + nextIndex] as HTMLInputElement[])[0]
			inputElement.focus()
		})
	}

	addFieldFromSelect (fieldId: string) {
		const field = this.definitionFields.find(f => f.field.id === fieldId)
		if (field) {
			this.addField(field)
		}
	}

	removeField (field: any, fieldIndex: number) {
		if (fieldIndex === -1) {
			return
		}

		if (field.postText) {
			if (fieldIndex === 0) {
				this.startText += field.postText
			} else {
				const previousField = this.fieldDisplay[fieldIndex - 1]
				previousField.postText += field.postText
			}
		}

		this.fieldDisplay.splice(fieldIndex, 1)
	}

	keydown (field: FieldInstance, index: number, event: KeyboardEvent) {
		if (this.readonly) {
			return
		}

		const caretPosition = (event.target! as HTMLInputElement).selectionStart

		if (event.keyCode === 8 && this.allowFieldRemovals) {
			// Backspace

			if (caretPosition === 0) {
				if (field) {
					this.removeField(field, index)

					let inputElement: HTMLInputElement | null = null
					if (index === 0) {
						inputElement = this.$refs["text-input"] as HTMLInputElement
					} else {
						const focusIndex = index - 1
						inputElement = (this.$refs["text-input-" + focusIndex] as HTMLInputElement[])[0]
					}

					inputElement.focus()
					inputElement.setSelectionRange(inputElement.value.length, inputElement.value.length)
				}
			}
		} else if (event.keyCode === 46 && this.allowFieldRemovals) {
			// Delete

			if ((!field && caretPosition === this.startText.length) ||
				(field && caretPosition === field.postText!.length)) {
				if (!field) {
					if (this.fieldDisplay.length > 0) {
						this.removeField(this.fieldDisplay[0], 0)
					}
				} else {
					if (this.fieldDisplay.length > 0) {
						this.removeField(this.fieldDisplay[index + 1], index)
					}
				}
			}
		} else if (event.keyCode === 37) {
			// arrow left
			if (caretPosition === 0) {
				let inputElement: HTMLInputElement | null = null
				if (index === 0) {
					inputElement = this.$refs["text-input"] as HTMLInputElement
				} else {
					const focusIndex = index - 1
					inputElement = (this.$refs["text-input-" + focusIndex] as HTMLInputElement[])[0] as HTMLInputElement
				}

				inputElement.focus()
				inputElement.setSelectionRange(inputElement.value.length, inputElement.value.length)
				event.preventDefault()
			}
		} else if (event.keyCode === 39) {
			// arrow right
			if ((!field && caretPosition === this.startText.length) ||
				(field && caretPosition === field.postText!.length)) {
				let inputElement: HTMLInputElement | null = null
				if (!index) {
					inputElement = (this.$refs["text-input-0"] as HTMLInputElement[])[0] as HTMLInputElement
				} else {
					const focusIndex = index + 1
					inputElement = (this.$refs["text-input-" + focusIndex] as HTMLInputElement[])[0] as HTMLInputElement
				}

				inputElement.focus()
				inputElement.setSelectionRange(0, 0)
				event.preventDefault()
			}
		}
	}
}

